// Master-Timeline preview for STS. Schedules each MasterClip via Web Audio
// with inPoint/outPoint slice bounds, routed through the voice-gain bus.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MasterClip, STSTake } from '@shared/types';
import * as AudioCtx from '@/lib/audioContext';
import {
  ensureVoiceGain,
  getAudioContext,
  invalidatePreviewAudio,
  isSafariMediaStreamPath,
  measureOutputLatency,
  onPreviewAudioInvalidate,
  setPreviewOutputLatency,
  startSafariAudioTap,
  stopAllPreviewSources,
  trackPreviewSource,
  warmupSilentBuffer,
} from '@/lib/audioContext';
import { getActiveVideo, seekVideoTo } from '@/lib/activeVideo';
import { useMixerStore, effectiveVoiceVolume } from '@/stores/mixer';
import { useSessionStore } from '@/stores/session';
import { useVideoDucking } from '@/hooks/useVideoDucking';
import { resolveBedPlacement } from '@/lib/bedPlacement';
import {
  decodeBedBuffer,
  getCachedBedBuffer,
  scheduleBed,
  stopBedSource,
} from '@/lib/sfxBedRouting';
import {
  onStopPreview,
  requestCancelPipeline,
} from '@/lib/pipelineCoordinator';
import {
  clearPlayback,
  registerPlaybackStop,
  startPlayback,
} from '@/stores/playback';

interface ScheduleEntry {
  clipId: string;
  takeId: string;
  offset: number;        // seconds on the master timeline
  inPoint: number;       // start within the take
  outPoint: number;      // end within the take
}

interface PreviewInternals {
  playing: boolean;
  baseContextTime: number;
  baseElapsed: number;
  schedule: ScheduleEntry[];
  totalDuration: number;
  /** Display asset length (getAssetTimelineDuration boundary) — the length beds resolve against. NOT the voice-widened totalDuration. */
  bedAssetLen: number;
  cancel: boolean;
  /** Last-rendered active clip id (for duck/unduck edge detection). */
  lastActiveClip: string | null;
  rafHandle: number | null;
  /** Pending setTimeout handles (deferred video.play() after latency).
   *  stop() must clear these so a deferred call can't fire after abort. */
  pendingTimeouts: number[];
}

function newInternals(): PreviewInternals {
  return {
    playing: false,
    baseContextTime: 0,
    baseElapsed: 0,
    schedule: [],
    totalDuration: 0,
    bedAssetLen: 0,
    cancel: false,
    lastActiveClip: null,
    rafHandle: null,
    pendingTimeouts: [],
  };
}

function contextNeedsRebuild(ac: AudioContext): boolean {
  return ac.state === 'closed' || ac.state === 'interrupted';
}

export function useSTSMasterPreview(
  clips: MasterClip[],
  takesById: Record<string, STSTake>,
  totalDuration: number,
  bedAssetLen: number,
) {
  const ref = useRef<PreviewInternals>(newInternals());
  const [isPlaying, setIsPlayingState] = useState<boolean>(false);
  const [playheadSec, setPlayheadSec] = useState<number>(0);

  // Hook-local decoded-buffer cache keyed by takeId. Takes in the store are
  // immutable, so we can't cache on the take itself the way TTS does.
  const bufferCacheRef = useRef<Map<string, AudioBuffer>>(new Map());

  const { duck, unduck } = useVideoDucking();

  useEffect(() => {
    const off = onPreviewAudioInvalidate(() => {
      bufferCacheRef.current.clear();
    });
    return off;
  }, []);

  const ensureTakeDecoded = useCallback(
    async (take: STSTake, ac: AudioContext): Promise<AudioBuffer | null> => {
      const cached = bufferCacheRef.current.get(take.id);
      if (cached) return cached;
      if (!take.audioBuffer) return null;
      try {
        const copy = take.audioBuffer.slice(0);
        const decoded = await ac.decodeAudioData(copy);
        bufferCacheRef.current.set(take.id, decoded);
        return decoded;
      } catch (e) {
        console.warn('[sts-preview] decodeAudioData failed for take', take.id, e);
        return null;
      }
    },
    [],
  );

  const scheduleFrom = useCallback((startFrom: number) => {
    const r = ref.current;
    const ac = getAudioContext();

    const voiceGain = ensureVoiceGain(
      effectiveVoiceVolume(useMixerStore.getState()),
    );
    stopAllPreviewSources();

    r.baseContextTime = ac.currentTime;
    r.baseElapsed = startFrom;

    let latency = measureOutputLatency(ac);
    if (isSafariMediaStreamPath()) latency = 0;
    setPreviewOutputLatency(latency);

    // Anchor every src.start() at the SAME context-time snapshot
    // (r.baseContextTime, captured above) so voice clips and bed share a
    // single start moment — see useTimelinePreview.ts for the full rationale.
    const anchor = r.baseContextTime;

    // Bed FIRST, mirroring useTimelinePreview: the voice loop's per-iteration
    // JS work was pushing the bed's effective start audibly behind voice.
    // Same shared `anchor` keeps bed + voice alignment exact.
    stopBedSource();
    const session = useSessionStore.getState();
    const activeBeds = session.sfxBeds[session.currentAssetIdx] ?? [];
    activeBeds.forEach((bed, idx) => {
      const trackIdx = (idx === 0 ? 0 : 1) as 0 | 1;
      const cached = getCachedBedBuffer(ac, bed.buffer, trackIdx);
      if (cached) {
        const placement = resolveBedPlacement(bed, r.bedAssetLen);
        scheduleBed(ac, cached, placement, startFrom, anchor, trackIdx);
      }
    });

    for (const entry of r.schedule) {
      const buffer = bufferCacheRef.current.get(entry.takeId);
      if (!buffer) continue;
      const clipLen = Math.max(0, entry.outPoint - entry.inPoint);
      if (clipLen <= 0) continue;
      const clipEnd = entry.offset + clipLen;
      if (clipEnd <= startFrom) continue;

      const src = ac.createBufferSource();
      src.buffer = buffer;
      if (voiceGain.context === ac) {
        src.connect(voiceGain);
      } else {
        src.connect(ac.destination);
      }
      trackPreviewSource(src);

      if (entry.offset <= startFrom) {
        const intoClip = startFrom - entry.offset;
        const bufferOffset = entry.inPoint + intoClip;
        const length = clipLen - intoClip;
        if (length > 0) src.start(anchor, bufferOffset, length);
      } else {
        src.start(
          anchor + (entry.offset - startFrom),
          entry.inPoint,
          clipLen,
        );
      }
    }

    startSafariAudioTap();
  }, []);

  const runRaf = useCallback(() => {
    const r = ref.current;
    const ac = getAudioContext();

    const finish = () => {
      stopAllPreviewSources();
      stopBedSource();
      // Clear deferred video.play() handles from the latency window.
      for (const h of r.pendingTimeouts) {
        try { window.clearTimeout(h); } catch { /* ignore */ }
      }
      r.pendingTimeouts = [];
      if (r.lastActiveClip !== null) {
        unduck();
        r.lastActiveClip = null;
      }
      r.playing = false;
      r.cancel = false;
      r.rafHandle = null;
      setIsPlayingState(false);
      const v = getActiveVideo();
      if (v && !v.paused) {
        try { v.pause(); } catch { /* ignore */ }
      }
      // Release the global playback mutex slot if it's still ours.
      clearPlayback('timeline');
    };

    const tick = () => {
      if (r.cancel) {
        finish();
        return;
      }
      const latency = isSafariMediaStreamPath() ? 0 : measureOutputLatency(ac);
      const raw = r.baseElapsed + (ac.currentTime - r.baseContextTime) - latency;
      const elapsed = Math.max(0, raw);
      setPlayheadSec(elapsed);

      let activeClip: string | null = null;
      for (const entry of r.schedule) {
        const clipLen = Math.max(0, entry.outPoint - entry.inPoint);
        if (raw >= entry.offset && raw < entry.offset + clipLen) {
          activeClip = entry.clipId;
          break;
        }
      }
      if (activeClip !== r.lastActiveClip) {
        if (activeClip !== null && r.lastActiveClip === null) {
          // duck() already pushes the attack ramp to all bed tracks via
          // applyBedGainExternalAll. Calling applyBedGain() instant here
          // would cancel the ramp.
          duck();
        } else if (activeClip === null && r.lastActiveClip !== null) {
          unduck();
        }
        r.lastActiveClip = activeClip;
      }

      if (r.totalDuration > 0 && elapsed >= r.totalDuration) {
        setPlayheadSec(0);
        finish();
        return;
      }

      r.rafHandle = requestAnimationFrame(tick);
    };

    r.rafHandle = requestAnimationFrame(tick);
  }, [duck, unduck]);

  const play = useCallback(
    async (startFrom = 0) => {
      const r = ref.current;
      if (r.playing) return;

      requestCancelPipeline();

      // Claim the global playback mutex — fires the stop handlers of other
      // kinds (take lanes, version-bar) so nothing plays over the master
      // mix. Mirrors useTimelinePreview (TTS).
      startPlayback('timeline');

      // Build the schedule from the current clip snapshot.
      const entries: ScheduleEntry[] = [];
      for (const clip of clips) {
        const take = takesById[clip.takeId];
        if (!take || !take.audioBuffer) continue;
        if (clip.outPoint - clip.inPoint <= 0) continue;
        entries.push({
          clipId: clip.id,
          takeId: clip.takeId,
          offset: clip.offset,
          inPoint: clip.inPoint,
          outPoint: clip.outPoint,
        });
      }
      entries.sort((a, b) => a.offset - b.offset);

      r.schedule = entries;
      r.totalDuration = totalDuration;
      r.bedAssetLen = bedAssetLen;
      r.cancel = false;
      r.lastActiveClip = null;

      let ac = getAudioContext();
      if (ac.state === 'suspended') {
        try { await ac.resume(); } catch { /* ignore */ }
      }
      if (contextNeedsRebuild(ac)) {
        invalidatePreviewAudio('context not running on play');
        ac = getAudioContext();
        if (ac.state === 'suspended') {
          try { await ac.resume(); } catch { /* ignore */ }
        }
      }

      if (ac.currentTime < 0.05) {
        try {
          await warmupSilentBuffer();
        } catch { /* ignore */ }
      }

      // Decode once per distinct take referenced by the schedule.
      const uniqueTakeIds = Array.from(new Set(entries.map((e) => e.takeId)));
      const decodes: Promise<unknown>[] = uniqueTakeIds.map((id) => {
        const t = takesById[id];
        return t ? ensureTakeDecoded(t, ac) : Promise.resolve(null);
      });
      // Decode every bed track (max 2) alongside takes. Cached per-track
      // via sfxBedRouting; scheduleFrom reads via getCachedBedBuffer.
      const session = useSessionStore.getState();
      const beds = session.sfxBeds[session.currentAssetIdx] ?? [];
      beds.forEach((bed, idx) => {
        if (!bed.buffer) return;
        const trackIdx = (idx === 0 ? 0 : 1) as 0 | 1;
        decodes.push(decodeBedBuffer(ac, bed.buffer, trackIdx));
      });
      if (decodes.length > 0) {
        await Promise.allSettled(decodes);
      }

      if (r.cancel) return;

      // CRITICAL: await the <video> seek before scheduling. See comment in
      // useTimelinePreview.ts — same rationale. Without awaiting, a play(0)
      // following a stop() at currentTime>0 emits a brief native-audio
      // snippet from the OLD playhead before the element snaps to 0.
      const v = getActiveVideo();
      const hasVideo = !!v && v.readyState >= 1 && Number.isFinite(v.duration);
      if (hasVideo && v) {
        await seekVideoTo(v, startFrom);
        // NOTE: we deliberately do NOT mute the <video> element here.
        // Forcing v.muted = true caused frame stutter in some browsers
        // (the audio decode appears to participate in the playback clock).
        // Trade-off: the video's native audio track plays in parallel with
        // the Web Audio voice/bed mix. Mixer-driven volume on the <video>
        // (via useVideoDucking) still applies, so users can attenuate it.
        // A cleaner fix would route the element through a
        // MediaElementAudioSourceNode, but that's a larger change.
      }

      // After-await re-check: stop() may have landed during seekVideoTo.
      if (r.cancel) return;

      scheduleFrom(startFrom);

      if (hasVideo && v) {
        const startVideo = () => {
          // If stop() landed during the latency window, don't restart the
          // <video> — that's the source of the post-stop tail snippet.
          //
          // Only r.cancel is checked here, NOT !r.playing. The latency==0
          // branch below calls startVideo() synchronously while r.playing is
          // still false (set a few lines down), so a !r.playing guard would
          // silently skip v.play() on every small-latency click — exactly
          // the regression that broke video preview entirely. r.cancel alone
          // is the correct abort signal: stop() sets it.
          if (r.cancel) return;
          v.play().catch(() => {
            /* autoplay blocked — ignore */
          });
        };
        const latency = AudioCtx.previewOutputLatency;
        if (latency > 0.005) {
          const h = window.setTimeout(startVideo, latency * 1000);
          r.pendingTimeouts.push(h);
        } else {
          startVideo();
        }
      }

      r.playing = true;
      setIsPlayingState(true);
      runRaf();
    },
    [clips, takesById, totalDuration, bedAssetLen, ensureTakeDecoded, scheduleFrom, runRaf],
  );

  const stop = useCallback(() => {
    const r = ref.current;
    r.cancel = true;
    if (r.rafHandle !== null) {
      cancelAnimationFrame(r.rafHandle);
      r.rafHandle = null;
    }
    // Clear deferred video.play() handles before they can fire after stop —
    // that timeout is what produces the residual audio tail post-stop.
    for (const h of r.pendingTimeouts) {
      try { window.clearTimeout(h); } catch { /* ignore */ }
    }
    r.pendingTimeouts = [];
    stopAllPreviewSources();
    stopBedSource();
    if (r.lastActiveClip !== null) {
      unduck();
      r.lastActiveClip = null;
    }
    r.playing = false;
    setIsPlayingState(false);
    const v = getActiveVideo();
    if (v && !v.paused) {
      try { v.pause(); } catch { /* ignore */ }
    }
    clearPlayback('timeline');
  }, [unduck]);

  const seek = useCallback(
    (sec: number) => {
      const r = ref.current;
      const clamped = Math.max(0, Math.min(totalDuration || sec, sec));
      if (r.playing) {
        stop();
        void play(clamped);
      } else {
        setPlayheadSec(clamped);
        const v = getActiveVideo();
        if (v && Number.isFinite(v.duration)) {
          try { v.currentTime = Math.min(clamped, v.duration || 0); } catch { /* ignore */ }
        }
      }
    },
    [play, stop, totalDuration],
  );

  useEffect(() => {
    const off = onStopPreview(() => { stop(); });
    return off;
  }, [stop]);

  // Register with the global mutex so stopAllPlayback() (Upload modal,
  // BottomBar) and startPlayback() of other kinds silence this preview.
  useEffect(() => registerPlaybackStop('timeline', stop), [stop]);

  useEffect(() => {
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => ({ play, stop, seek, isPlaying, playheadSec }),
    [play, stop, seek, isPlaying, playheadSec],
  );
}
